import { supabase } from './supabase';

export const SEATS_FULL = "You've used all your seats. Contact Carduzo to add more.";

/** Calls the company-members Edge Function and surfaces its error message. */
export async function companyCall(action, orgId, payload = {}) {
  const { data, error } = await supabase.functions.invoke('company-members', {
    body: { action, org_id: orgId, ...payload },
  });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  return data;
}

/** Members, open invites and unattached cards of a company (company_people RPC). */
export async function getPeople(orgId) {
  const { data, error } = await supabase.rpc('company_people', { p_org: orgId });
  if (error) throw error;
  return data || [];
}

/** Views, saves and leads per card over the last `days` days (company_activity RPC). */
export async function getActivity(orgId, days = 30) {
  const { data, error } = await supabase.rpc('company_activity', { p_org: orgId, p_days: days });
  if (error) throw error;
  return data || [];
}

/** Seats in use: active members + pending (not expired) invites. */
export const seatsUsed = (people) =>
  people.filter((p) => (p.kind === 'member' && p.status === 'active') || (p.kind === 'invite' && p.status === 'pending')).length;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Parse pasted bulk-invite text. One person per line: "Name, email", "Name <email>",
 * "email, Name", "email" — tabs (from spreadsheets) and semicolons work as separators too.
 * Returns [{ line, full_name, email, error }].
 */
export function parseBulkInvites(text, existingEmails = []) {
  const taken = new Set(existingEmails.map((e) => (e || '').toLowerCase()));
  const seen = new Set();
  return text
    .split(/\r?\n/)
    .map((raw, i) => ({ raw: raw.trim(), line: i + 1 }))
    .filter((r) => r.raw)
    .map(({ raw, line }) => {
      const angle = raw.match(/^(.*?)<([^>]+)>\s*$/);
      let name = '';
      let email = '';
      if (angle) {
        name = angle[1].replace(/[,;\t]+$/, '').trim();
        email = angle[2].trim();
      } else {
        const parts = raw.split(/[,;\t]/).map((p) => p.trim()).filter(Boolean);
        const at = parts.findIndex((p) => p.includes('@'));
        if (at >= 0) {
          email = parts[at];
          name = parts.filter((_, j) => j !== at).join(' ');
        } else {
          name = parts.join(' ');
        }
      }
      name = name.replace(/^["']|["']$/g, '').trim().slice(0, 120);
      email = email.toLowerCase();
      let error = '';
      if (!email) error = 'No email address on this line';
      else if (!EMAIL_RE.test(email)) error = 'Not a valid email address';
      else if (seen.has(email)) error = 'Listed twice';
      else if (taken.has(email)) error = 'Already in the company or invited';
      if (email) seen.add(email);
      return { line, full_name: name, email, error };
    });
}
